import {classes} from '@bitstillery/common/lib/utils'
import {MithrilComponent} from '@bitstillery/mithril'
import {Button} from '@bitstillery/common/components/ui/button/button'
import {Icon} from '@bitstillery/common/components/ui/icon/icon'
import {blob_to_base64, present, unique_id} from '@bitstillery/common/lib/utils'
import {state} from '@bitstillery/mithril'
import i18next from 'i18next'
import {logger} from '@bitstillery/mithril'
import m from '@bitstillery/mithril'
import {failure_message} from '@bitstillery/common/lib/validation'
import type {Validator} from '@bitstillery/common/lib/validation'

interface FieldUploadAttrs {
    accept?: string
    disabled?: boolean | [object | undefined, string]
    model: {
        data: string | null
        /** Unset for a snapshot, which is a bare Blob rather than a File. */
        name: string | undefined
        content_type: string
    }
    help?: string
    label?: string
    placeholder?: string
    icon?: string
    className?: string
    type?: 'file' | 'screenshot'

    onafter_media?: (() => unknown) | undefined
    onbefore_media?: (() => unknown) | undefined

    validation?: Validator | undefined
}

/** A picked or pasted File, or the unnamed Blob a snapshot produces. */
type UploadBlob = Blob & {name?: string}

export class FieldUpload extends MithrilComponent<FieldUploadAttrs> {
    data = state(
        {
            blob_url: '',
        },
        'field-upload-data',
    )

    $container: HTMLElement | null = null
    canvas = document.createElement('canvas')
    id = unique_id()
    video = document.createElement('video')
    onpaste_listener: (e: ClipboardEvent) => void = () => {}
    ondragover_listener: (e: DragEvent) => void = () => {}
    ondrop_listener: (e: DragEvent) => void = () => {}

    prepare_file(vnode: m.Vnode<FieldUploadAttrs>, blob: UploadBlob) {
        logger.info('[file-upload] prepare file')
        if (vnode.attrs.validation) {
            vnode.attrs.validation.dirty = true
        }

        vnode.attrs.model.name = blob.name
        vnode.attrs.model.content_type = blob.type

        if (vnode.attrs.type === 'screenshot') {
            if (!blob.type.startsWith('image/')) {
                return
            }
            void this.prepare_screenshot(vnode, blob)
        } else {
            const file_reader = new FileReader()
            file_reader.addEventListener('load', () => {
                vnode.attrs.model.data = typeof file_reader.result === 'string' ? file_reader.result : ''
            })
            file_reader.readAsDataURL(blob)
        }
    }

    async prepare_screenshot(vnode: m.Vnode<FieldUploadAttrs>, blob: UploadBlob) {
        logger.info('[file-upload] prepare_screenshot')

        if (blob.type.startsWith('video/')) {
            const video = document.createElement('video')
            video.src = URL.createObjectURL(blob)
            video.addEventListener(
                'loadeddata',
                () => {
                    const canvas = document.createElement('canvas')
                    canvas.width = video.videoWidth
                    canvas.height = video.videoHeight
                    const ctx = canvas.getContext('2d')
                    if (ctx) {
                        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
                        const background_image = canvas.toDataURL('image/png')
                        if (this.$container) {
                            this.$container.style.backgroundImage = `url(${background_image})`
                        }
                    }

                    // Cleanup
                    URL.revokeObjectURL(video.src)
                },
                {once: true},
            )
        } else {
            this.data.blob_url = URL.createObjectURL(blob)
            if (this.$container) {
                this.$container.style.backgroundImage = `url(${this.data.blob_url})`
            }
        }
        vnode.attrs.model.content_type = blob.type

        const data = await blob_to_base64(blob)
        Object.assign(vnode.attrs.model, {
            data,
            name: blob.name,
        })
    }

    override oncreate(vnode: m.Vnode<FieldUploadAttrs>) {
        this.$container = document.querySelector<HTMLElement>(`#field-media-upload-${this.id} .js-snapshot`)

        this.onpaste_listener = (e) => {
            this.onpaste(e, vnode)
        }
        document.addEventListener('paste', this.onpaste_listener)

        this.ondragover_listener = (e) => {
            e.preventDefault()
        }
        this.ondrop_listener = (e) => {
            e.preventDefault()
            if (!e.dataTransfer) {
                return
            }
            const {files} = e.dataTransfer
            if (files.length !== 1) {
                return
            }
            this.prepare_file(vnode, present(files[0], 'dropped file'))
        }
        if (this.$container) {
            this.$container.addEventListener('dragover', this.ondragover_listener)
            this.$container.addEventListener('drop', this.ondrop_listener)
        }
    }

    override onremove() {
        document.removeEventListener('paste', this.onpaste_listener)
    }

    is_disabled(attrs: FieldUploadAttrs): boolean {
        if (Array.isArray(attrs.disabled)) {
            const [target, key] = attrs.disabled
            return Boolean(target && Reflect.get(target, key))
        }
        return attrs.disabled === true
    }

    onpaste(event: ClipboardEvent, vnode: m.Vnode<FieldUploadAttrs>) {
        if (this.is_disabled(vnode.attrs)) {
            return
        }

        const {items} = present(event.clipboardData, 'clipboard data')
        if (items.length !== 1) {
            return
        }
        const item = present(items[0], 'pasted item')
        // The listener is document-wide, so pasted text anywhere on the page arrives here too.
        if (item.kind !== 'file') {
            return
        }
        const blob = item.getAsFile()
        if (!blob) {
            return
        }
        this.prepare_file(vnode, blob)
    }

    view(vnode: m.Vnode<FieldUploadAttrs>) {
        const {validation} = vnode.attrs

        const invalid = validation ? validation._invalid : false
        const disabled = this.is_disabled(vnode.attrs)
        return (
            <div
                className={classes('c-field-media-upload', 'field', vnode.attrs.className, {
                    disabled,
                    invalid: validation && invalid && validation.dirty,
                    valid: validation && !invalid && validation.dirty,
                })}
                id={`field-media-upload-${this.id}`}
            >
                {vnode.attrs.label && (
                    <label>
                        {vnode.attrs.label}
                        {vnode.attrs.icon && <Icon name={vnode.attrs.icon} />}
                        {validation && <span className='validation'>{validation.label}</span>}
                    </label>
                )}

                <div className='media-area'>
                    <div className='actions'>
                        {vnode.attrs.type === 'screenshot' && (
                            <Button
                                icon='screenshot'
                                onclick={async () => {
                                    if (vnode.attrs.onbefore_media) {
                                        vnode.attrs.onbefore_media()
                                    }
                                    // Capture only this tab:
                                    const display_options = {
                                        preferCurrentTab: true,
                                        video: {
                                            displaySurface: 'browser',
                                        },
                                    }
                                    const stream = await navigator.mediaDevices.getDisplayMedia(display_options)
                                    this.video.srcObject = stream
                                    await this.video.play()
                                    // A frame can only be drawn from HAVE_FUTURE_DATA (3) on, which is when canplay fires.
                                    if (this.video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
                                        await new Promise((resolve) =>
                                            this.video.addEventListener('canplay', resolve, {once: true}),
                                        )
                                    }

                                    this.canvas.width = this.video.videoWidth
                                    this.canvas.height = this.video.videoHeight
                                    this.canvas
                                        .getContext('2d')
                                        ?.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height)
                                    if (vnode.attrs.onafter_media) {
                                        vnode.attrs.onafter_media()
                                    }
                                    // Close the screen sharing in this session.
                                    stream.getTracks().forEach((track) => track.stop())
                                    const file = await new Promise<Blob | null>((resolve) => {
                                        this.canvas.toBlob((blob) => resolve(blob), 'image/png')
                                    })
                                    if (!file) {
                                        return
                                    }
                                    this.prepare_file(vnode, file)
                                }}
                                tip={i18next.t('issues.button.label.take_snapshot')}
                                type='info'
                                variant='toggle'
                            />
                        )}
                        <Button
                            icon='upload'
                            tip={i18next.t('issues.button.label.select_image')}
                            type='info'
                            variant='toggle'
                            onclick={() => {
                                const file_input = document.createElement('input')
                                file_input.type = 'file'
                                file_input.accept = vnode.attrs.accept || ''
                                file_input.addEventListener('change', () => {
                                    if (!file_input.files || file_input.files.length !== 1) {
                                        return
                                    }
                                    this.prepare_file(vnode, present(file_input.files[0], 'chosen file'))
                                })

                                file_input.click()
                            }}
                        />
                        <Button
                            disabled={!vnode.attrs.model.data}
                            icon='trash'
                            type='info'
                            variant='toggle'
                            onclick={() => {
                                Object.assign(vnode.attrs.model, {
                                    data: '',
                                    file: '',
                                    name: '',
                                })
                                this.data.blob_url = ''
                                if (this.$container) {
                                    this.$container.style.backgroundImage = `url(${this.data.blob_url})`
                                }
                            }}
                        />
                    </div>
                    <div
                        className={classes('snapshot js-snapshot', {
                            'contains-image': this.data.blob_url,
                        })}
                    >
                        <Icon name={vnode.attrs.type === 'screenshot' ? 'image' : 'upload'} type='unset' />
                        <p>{vnode.attrs.model.name ? vnode.attrs.model.name : vnode.attrs.placeholder || ''}</p>
                    </div>
                </div>
                {(() => {
                    if (invalid && validation?.dirty) {
                        const message = invalid === true ? String(invalid) : failure_message(invalid)
                        return <div className='help validation'>{message}</div>
                    } else if (vnode.attrs.help) {
                        return <div className='help'>{vnode.attrs.help}</div>
                    }
                    return undefined
                })()}
            </div>
        )
    }
}
